import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionFooter,
  OptionFooterBanner,
  OptionIcon,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { RadioGroupItem } from "@babackapp/customer-ui/components/radio-group";
import { AnimatePresence, motion, type Variants } from "motion/react";
import { Receipt, WalletIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useDiscountBonus } from "@babackapp/return-widget-components/hooks/use-discount-bonus";
import { useRefundTagOverride } from "@babackapp/return-widget-components/hooks/use-refund-tag-override";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useRefundOption } from "../../hooks/use-refund-option";

const footerVariants: Variants = {
  hidden: { height: 0, transition: { duration: 0.2 } },
  visible: {
    height: "auto",
    transition: { ease: [0.25, 0.1, 0.25, 1], duration: 0.25, delay: 0.2 },
  },
};

const footerContentVariants: Variants = {
  hidden: { opacity: 0, rotateX: -60, y: 10 },
  visible: {
    opacity: 1,
    rotateX: 0,
    y: 0,
    transition: { type: "spring", delay: 0.3 },
  },
};

type Props = {
  selected: boolean;
  discountAvailable?: boolean;
};

export function BankTransferOption({ selected, discountAvailable = true }: Props) {
  const t = useCustomText();
  const { orderId, shopId } = useWidgetParams();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const bonusCents = useDiscountBonus(orderId, shopId, lineItemId);
  const hasBonus = (bonusCents ?? 0) > 0;
  const { amountText, feeText } = useRefundOption();
  const isTagOverride = useRefundTagOverride(orderId, lineItemId);
  return (
    <Label htmlFor="refund-type">
      <Option>
        <OptionIcon>
          <Receipt />
        </OptionIcon>
        <OptionTitle>{t("bankTransferOption.title")}</OptionTitle>
        <OptionDescription>
          {t("bankTransferOption.description")}
          {feeText && (
            <span className="block text-xs mt-0.5 opacity-75">{feeText}</span>
          )}
          {isTagOverride && (
            <span className="block text-xs mt-0.5 opacity-75">
              {t("bankTransferOption.merchantOverride")}
            </span>
          )}
        </OptionDescription>
        <OptionAction className="justify-end gap-3">
          <span className="font-sans text-sm tabular-nums whitespace-nowrap">
            {amountText}
          </span>
          <RadioGroupItem value="bank-transfer" id="refund-type" />
        </OptionAction>
        <OptionFooter className="[perspective:600px]">
          <AnimatePresence>
            {selected && discountAvailable && hasBonus && (
              <motion.div
                variants={footerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="mt-2"
              >
                <motion.div variants={footerContentVariants} className="origin-top">
                  <OptionFooterBanner>
                    <WalletIcon className="size-4" />
                    <span className="flex-1">
                      {t("bankTransferOption.nudge")}
                    </span>
                  </OptionFooterBanner>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </OptionFooter>
      </Option>
    </Label>
  );
}
